import { useEffect, useState } from 'react';
import { CreditCard, Check, TrendingUp, AlertCircle } from 'lucide-react';
import { analyticsApi } from '../../services/api';

/**
 * Billing plans — WIREFRAME.
 *
 * Nothing on this tab charges anyone. It is the working draft of the
 * commercial model so pricing can be argued about against real unit
 * economics (pulled live from the cost telemetry below). The launch
 * checklist at the bottom tracks what the billing layer still needs
 * before a paying customer can exist.
 */

const PLANS = [
  {
    name: 'Trial',
    price: 'Free',
    cadence: '',
    tagline: 'Evaluate with real cases',
    features: ['20 reports included', 'All exam categories', 'Copy / RTF export', 'Email support'],
    highlight: false,
  },
  {
    name: 'Solo Radiologist',
    price: 'A$495',
    cadence: '/month',
    tagline: 'One radiologist, unlimited reporting',
    features: [
      'Unlimited reports (fair use)',
      'Personal style learning engine',
      'Karisma Bridge integration',
      'Voice shortcuts & templates',
      'Priority support',
    ],
    highlight: true,
  },
  {
    name: 'Practice',
    price: 'A$395',
    cadence: '/radiologist/month',
    tagline: '3+ radiologists, shared practice',
    features: [
      'Everything in Solo',
      'Practice admin console',
      'Pooled usage analytics',
      'Centralised billing',
      'Volume discount at 10+ seats',
    ],
    highlight: false,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    cadence: '',
    tagline: 'Teleradiology groups & hospitals',
    features: [
      'PACS / RIS integration',
      'SSO (Cognito / SAML)',
      'Data residency & BAA',
      'SLA + dedicated support',
      'Custom model tuning',
    ],
    highlight: false,
  },
];

const LAUNCH_CHECKLIST = [
  { item: 'Server-side credit enforcement (decrement + block at 0) — currently credits are display-only', done: false },
  { item: 'Cognito logins with per-user identity (plan attaches to the account)', done: false },
  { item: 'Stripe integration: subscriptions, seat counts, invoices, GST', done: false },
  { item: 'Usage metering already captured per call (api_usage) — billing export needed', done: false },
  { item: 'Plan/entitlement model in the database (plans are hardcoded in this wireframe)', done: false },
  { item: 'Terms of service, privacy policy, medical-software disclaimers reviewed', done: false },
];

interface BurnRate {
  daily_avg_usd?: number;
  projected_month_usd?: number;
  cost_per_report_usd?: number;
  report_count?: number;
}

export default function Billing() {
  const [burn, setBurn] = useState<BurnRate | null>(null);

  useEffect(() => {
    analyticsApi.costBurnRate(30).then(setBurn).catch(() => setBurn(null));
  }, []);

  const costPerReport = burn?.cost_per_report_usd;

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
        <AlertCircle size={16} className="text-amber-600 dark:text-amber-400 flex-none mt-0.5" />
        <p className="text-sm text-amber-800 dark:text-amber-300">
          <b>Wireframe.</b> These plans are a pricing draft for discussion — nothing is billed, no payment
          integration exists yet. Edit freely; the launch checklist below is what stands between this page and
          real revenue.
        </p>
      </div>

      {/* Unit economics from live telemetry */}
      <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={16} className="text-primary-600" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Unit economics (live, last 30 days)</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {costPerReport != null ? `$${costPerReport.toFixed(2)}` : '—'}
            </div>
            <div className="text-xs text-gray-500">marginal cost / report (USD)</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {burn?.projected_month_usd != null ? `$${burn.projected_month_usd.toFixed(0)}` : '—'}
            </div>
            <div className="text-xs text-gray-500">projected monthly API spend (USD)</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {costPerReport != null ? `~${Math.round(495 / (costPerReport * 1.55 * 22))}×` : '—'}
            </div>
            <div className="text-xs text-gray-500">Solo plan gross margin multiple @ ~22 reports/day</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{burn?.report_count ?? '—'}</div>
            <div className="text-xs text-gray-500">reports in window</div>
          </div>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {PLANS.map((p) => (
          <div
            key={p.name}
            className={`rounded-lg border p-4 flex flex-col bg-white dark:bg-surface-900 ${
              p.highlight
                ? 'border-primary-400 dark:border-primary-600 ring-1 ring-primary-200 dark:ring-primary-900'
                : 'border-gray-200 dark:border-surface-800'
            }`}
          >
            {p.highlight && (
              <span className="self-start mb-2 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                LAUNCH FOCUS
              </span>
            )}
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{p.name}</h3>
            <div className="mt-1 mb-1">
              <span className="text-2xl font-bold text-gray-900 dark:text-white">{p.price}</span>
              <span className="text-xs text-gray-500">{p.cadence}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">{p.tagline}</p>
            <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-300">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-1.5">
                  <Check size={12} className="text-green-500 flex-none mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Launch checklist */}
      <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard size={16} className="text-primary-600" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            What the billing layer needs before launch
          </h3>
        </div>
        <ul className="space-y-2">
          {LAUNCH_CHECKLIST.map((c) => (
            <li key={c.item} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
              <span
                className={`w-4 h-4 flex-none mt-0.5 rounded border ${
                  c.done
                    ? 'bg-green-500 border-green-500'
                    : 'border-gray-300 dark:border-surface-600 bg-white dark:bg-surface-800'
                }`}
              >
                {c.done && <Check size={14} className="text-white" />}
              </span>
              {c.item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
